'use strict'

import { ConfigService, RunConfigService, events, storage } from '@nuskin/ns-util'
import { UserService, VGDistInfoService } from '@nuskin/ns-account'
import nsLoyalty from '@nuskin/ns-loyalty'

let loyaltyObj
let userId = ''

function checkExistingData(forceUpdate = false) {
    let user = UserService.getUser()

    // We need to clear the LOYALTY_DATA if the user is not logged in:
    if (!user) {
        storage.removeItem(storage.metadata.LOYALTY_DATA)
        return false
    }

    // If user is outside their market don't allow pointsto be used
    if (user.countryCd !== RunConfigService.getRunConfig().country) {
        storage.removeItem(storage.metadata.LOYALTY_DATA)
        return false
    }

    let loyaltyData = false

    if (forceUpdate) {
        storage.removeItem(storage.metadata.LOYALTY_DATA)
    } else {
        // If we have the item stored, use that instead
        loyaltyData = storage.getItem(storage.metadata.LOYALTY_DATA)
    }

    return loyaltyData
}

function createLoyaltyObject () {
    const user = UserService.getUser()

    const marketConfig = ConfigService.getMarketConfig()
    const runConfig = RunConfigService.getRunConfig()

    let configuration

    if (user) {
        if (loyaltyObj && userId === user.id) {
            return loyaltyObj
        }

        userId = user.id

        configuration = {
            requestUrl: marketConfig.awsUrl,
            market: user.countryCd,
            userId: user.id,
            jwt: "JWT " + user.eid,
            client_id: marketConfig.checkout.clientId,
            client_secret: marketConfig.checkout.clientSecret
        }
    } else {
        if (loyaltyObj && userId === '') {
            return loyaltyObj
        }

        userId = ''

        configuration = {
            requestUrl: marketConfig.awsUrl,
            market: runConfig.country,
            jwt: 'JWT',
            client_id: marketConfig.checkout.clientId,
            client_secret: marketConfig.checkout.clientSecret
        }
    }

    return (loyaltyObj = new nsLoyalty.NsLoyalty(configuration))
}

function WebLoyalty () {
    this.getAllLoyaltyInfo = async function() {
        createLoyaltyObject()

        return await loyaltyObj.getAllLoyaltyInfo()
    }

    this.getLoyaltyRewardsPoints = function(forceUpdate = false) {
        let loyaltyData = checkExistingData(forceUpdate)
        if(loyaltyData){
            return loyaltyData
        } else {
            this.getRewardsPoints(forceUpdate, false)
        }

        return false
    }

    this.getRewardsPoints = async function(forceUpdate = false, checkExisting = true) {
        let loyaltyData = false

        if(checkExisting){
            loyaltyData = checkExistingData(forceUpdate)
        }

        loyaltyObj = createLoyaltyObject()

        let user = UserService.getUser()
        // We need to clear the LOYALTY_DATA if the user is not logged in:
        if (!user) {
            return false
        }

        // If user is outside their market don't allow pointsto be used
        if (user.countryCd !== RunConfigService.getRunConfig().country) {
            return false
        }

        if (!loyaltyData) {
            return loyaltyObj.getRewardsBucketsAndCurrentTierData().then(loyaltyResponse => {
                storage.setItem(storage.metadata.LOYALTY_DATA, loyaltyResponse)
                events.publish(events.loyalty.LOYALTY_DATA_UPDATED, loyaltyResponse)

                return loyaltyResponse
            })
        }

        if(forceUpdate){
            events.publish(events.loyalty.LOYALTY_DATA_UPDATED, loyaltyData)
        }

        return loyaltyData
    }

    this.getMarketConfig = async function() {
        createLoyaltyObject()

        return await loyaltyObj.getMarketConfig()
    }

    /**
     * Checks market config and loyalty config to determine if the signup will be allowed
     *   to opt in to the loyalty program.
     * @param {String} market - The market of the signup
     * @param {String} signupType - The signup type (BRAND_AFFILIATE, MEMBER, CUSTOMER) 
     * @return {boolean} - Whether the opt in is available for the market + signupType combination
     */
    this.loyaltyOptInAvailableForSignup = async function(market, signupType) {
        // We need to use our specific market and then restore the 
        // runConfig country afterwards
        const saveCountry = RunConfigService.getRunConfig().country;
        const marketConfig = ConfigService.getMarketConfig(false, market);
        ConfigService.getMarketConfig(false, saveCountry);

        if (!marketConfig.loyalty.enabled) {
            return false
        }

        const loyaltyConfig = {
            requestUrl: marketConfig.awsUrl,
            market: market,
            jwt: 'JWT',
            client_id: marketConfig.checkout.clientId,
            client_secret: marketConfig.checkout.clientSecret
        }

        const localLoyaltyObject = new nsLoyalty.NsLoyalty(loyaltyConfig)

        const loyaltyMarketConfig = await localLoyaltyObject.getMarketConfig()
        
        let optInAvailable = false
        switch(signupType) {
            case 'BRAND_AFFILIATE':
                optInAvailable = loyaltyMarketConfig.entityTypes.dist ? true : false
                break;
            case 'CUSTOMER':
            case 'MEMBER':
                optInAvailable = (loyaltyMarketConfig.entityTypes.cust || 
                    loyaltyMarketConfig.entityTypes.pref)
                break;
        }

        return optInAvailable
    }

    this.loyaltyCanOptIn = async function(flags = {}) {
        let marketConfig = ConfigService.getMarketConfig()
        if (!marketConfig.loyalty.enabled) {
            return false
        }

        let user = UserService.getUser()
        let customerCanOptIn = false
        let distributorCanOptIn = false
        let loyaltyData = checkExistingData()
    
        // Check if user is logged in.
        if (!user) {
            return false
        }

        // We also need to make sure we are in the user's home market:
        if (user.countryCd !== RunConfigService.getRunConfig().country) {
            return false;
        }

        if (loyaltyData && typeof loyaltyData.canOptIn == 'boolean') {
            return loyaltyData.canOptIn
        }

        createLoyaltyObject()

        // We need to get the market config and check against the user status to see if they can
        // sign up.
        let currentParticipantLevel = 0
        let maximumParticipantLevel = 0
        await loyaltyObj.getMarketConfig().then(loyaltyResponse => {
            customerCanOptIn = loyaltyResponse.entityTypes.cust || loyaltyResponse.entityTypes.pref
            distributorCanOptIn = loyaltyResponse.entityTypes.dist

            maximumParticipantLevel = loyaltyResponse.titleParticipant;
        })

        // Note: 02/03/2020
        // We will assume the user is a distributor if possiblyDistributorSignup is true.
        let isDistributor = user.isDistributor() || flags.possiblyDistributorSignup

        if ((!customerCanOptIn && !isDistributor) || (!distributorCanOptIn && isDistributor)) {
            if (loyaltyData) {
                loyaltyData.canOptIn = false
                storage.setItem(storage.metadata.LOYALTY_DATA, loyaltyData)
            }   
            return false;
        }

        // We need to look at the current user's participant level and if it is below the market
        // config number, then they can sign up.
        await VGDistInfoService.getVGDistInfo().then(responseValues => {
            if (responseValues != null && responseValues.data != null) {
                let rank = parseInt(responseValues.data.subRank);

                currentParticipantLevel = isNaN(rank) ? 0 : rank;
            }
        })

        if (currentParticipantLevel < maximumParticipantLevel) {

            if (loyaltyData) {
                loyaltyData.canOptIn = true
                storage.setItem(storage.metadata.LOYALTY_DATA, loyaltyData)
            }
            
            return true;
        }

        if (loyaltyData) {
            loyaltyData.canOptIn = false
            storage.setItem(storage.metadata.LOYALTY_DATA, loyaltyData)
        }

        return false;
    }

    this.loyaltyOptIn = async function() {
        if(!UserService.isLoggedIn()){
            return {success: false, message: 'No user available.'};
        }

        createLoyaltyObject()

        let optedInStatus = false;

        await loyaltyObj.getUserOptInStatus().then(loyaltyResponse => {
            optedInStatus = loyaltyResponse.optInStatus;
        });

        if(!optedInStatus) {
            //We need to get the user info to get them opted in:
            let adrStatus = 'NO';
            let titleParticipantStatus = 'NO';

            // I know this is a horrible choice. Please forgive me for my sins. -- Ryan Kell
            await aem.AdrService.getAdrList().then(adrResponse => {
                if(adrResponse.length > 0){
                    adrStatus = 'YES';
                }
            })

            await this.loyaltyCanOptIn().then(responseValues => {
                if(responseValues === true){
                    titleParticipantStatus = 'NO';
                } else {
                    titleParticipantStatus = 'YES';
                }
            })

            let optInConfig = {
                adrParticipant: adrStatus,
                titleParticipant: titleParticipantStatus
            }

            if(UserService.getUser().isCustomer()){
                optInConfig.entityType = 'CUST';
            } else if(UserService.getUser().isPreferredCustomer()){
                optInConfig.entityType = 'PREF';
            } else if(UserService.getUser().isDistributor()){
                optInConfig.entityType = 'DIST';
            }

            await loyaltyObj.optInUser(optInConfig).then(loyaltyResponse => {
                optedInStatus = loyaltyResponse;
            })
        }

        this.getRewardsPoints(true);

        return {success: true, optInStatus: optedInStatus};
    }

    this.loyaltyOptOut = async function() {
        createLoyaltyObject()

        if(!UserService.isLoggedIn()){
            return {success: false, message: 'No user available.'};
        }

        let optedInStatus = false;

        await loyaltyObj.getUserOptInStatus().then(loyaltyResponse => {
            optedInStatus = loyaltyResponse.optInStatus;
        });

        if(optedInStatus) {
            await loyaltyObj.optOutUser().then(loyaltyResponse => {
                // The return value is a success response, so if we get true, the user was successfully updated,
                // meaning their optedInStatus = false
                // The opposite is true - if we get a false response, we did not successfully set the user to opted out,
                // so it would be optedInStatus = true
                if(loyaltyResponse === true){
                    optedInStatus = false;
                } else {
                    optedInStatus = true;
                }
            })
        }

        this.getRewardsPoints(true);

        return {success: true, optInStatus: optedInStatus};
    }

    this.getViewedTandC = async function() {

        let user = UserService.getUser()

        // We also need to make sure we are in the user's home market:
        if (user.countryCd !== RunConfigService.getRunConfig().country) {
            return false;
        }

        createLoyaltyObject()

        if (loyaltyObj) {
            return await loyaltyObj.getViewedTandC()
        }

        return false
    }

    this.setUserViewedTandC = async function() {
        createLoyaltyObject()

        if (loyaltyObj) {
            return await loyaltyObj.setUserViewedTandC()
        }

        return false
    }

    events.subscribe(events.loyalty.UPDATE_LOYALTY_DATA, () => {
        this.getRewardsPoints(true)
    })
}

let webLoyalty = new WebLoyalty()

export default webLoyalty
